import React, { useRef } from 'react'
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { Add, Close, DragHandle, ExpandMore } from '@mui/icons-material'
import { v4 } from 'uuid'

import useCustomFields from '../hooks/api/useCustomFields'
import {
  MAX_ALLOWED_CUSTOM_FIELDS,
  MAX_ALLOWED_CUSTOM_FIELD_OPTIONS,
} from '../constants'
import SettingsFooter from './shared/SettingsFooter'
import useAlert from '../hooks/useAlert'
import { DndProvider, useDrag } from 'react-dnd'
import useIsMobile from '../hooks/useIsMobile'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import Drop from '../common/ListEditor/Drop'
import { ItemTypes } from '../utils/dnd/constants'
import DragAndDropInstructions from './shared/DragAndDropInstructions'

export const CustomFieldsForm = ({ fieldState, setFieldState, types }) => {
  const handleAddField = () => {
    setFieldState((prevState) => [
      ...prevState,
      {
        id: v4(),
        name: `Field ${fieldState.length + 1}`,
        typeId: types[0].id,
        options: [],
      },
    ])
  }

  const handleFieldNameChange = (value, id) => {
    setFieldState((prevState) =>
      prevState.map((field) => {
        if (field.id === id) {
          return {
            ...field,
            name: value,
          }
        }
        return field
      })
    )
  }

  const handleDeleteField = (id) => {
    setFieldState((prevState) => prevState.filter((field) => field.id !== id))
  }

  const handleDeleteOption = (fieldId, optionId) => {
    setFieldState((prevState) =>
      prevState.map((f) => {
        if (f.id !== fieldId) return f

        return {
          ...f,
          options: f.options.filter((o) => o.id !== optionId),
        }
      })
    )
  }

  const handleTypeUpdate = (e, id) => {
    const { value } = e.target
    const selectedType = types.find((t) => t.id === value)
    const needsOptions = ['dropdown', 'checkbox'].includes(
      selectedType?.dataType
    )
    setFieldState((prevState) =>
      prevState.map((field) => {
        if (field.id === id) {
          return {
            ...field,
            typeId: value,
            optionsOpen: needsOptions,
            options:
              needsOptions && field.options.length === 0
                ? [
                    {
                      id: v4(),
                      name: 'Option 1',
                    },
                  ]
                : field.options,
          }
        }
        return {
          ...field,
          optionsOpen: needsOptions ? false : field.optionsOpen,
        }
      })
    )
  }

  const updateOption = (fieldId, optionId, newValues) => {
    setFieldState((prevState) =>
      prevState.map((f) => {
        if (f.id !== fieldId) return f

        return {
          ...f,
          options: f.options.map((o) => {
            if (o.id !== optionId) return o

            return {
              ...o,
              ...newValues,
            }
          }),
        }
      })
    )
  }

  const updateField = (id, newValues) => {
    setFieldState((prevState) =>
      prevState.map((f) => {
        if (f.id !== id) return f

        return {
          ...f,
          ...newValues,
        }
      })
    )
  }

  const onDrop = (item, id) => {
    const filtered = fieldState.filter((li) => li.id !== item.id)

    const ids = filtered.map((li) => li.id)
    const insertAfterIndex = ids.indexOf(id)

    const spliced = filtered
    spliced.splice(insertAfterIndex + 1, 0, item)

    setFieldState(spliced)
  }

  const isMobile = useIsMobile()

  return (
    <>
      <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
        <Box sx={{ my: 3 }}>
          {fieldState.map((field, index) => {
            const selectedType = types.find((t) => t.id === field.typeId)
            const needsOptions = ['dropdown', 'checkbox'].includes(
              selectedType?.dataType
            )

            return (
              <DragWrapper
                item={field}
                list={fieldState}
                onDrop={onDrop}
                key={field.id}
                i={index}
              >
                <Box
                  sx={{
                    my: 2,
                    border: '1px solid #c1bfbf',
                    borderRadius: '4px',
                    p: 2,
                    backgroundColor: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{ fontWeight: 'bold', mr: 2, width: '20px' }}
                    >
                      {index + 1}
                    </Typography>

                    <TextField
                      sx={{ mr: 2 }}
                      fullWidth
                      select
                      value={field.typeId}
                      label={'Field Type'}
                      onChange={(e) => handleTypeUpdate(e, field.id)}
                    >
                      {types.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      value={field.name}
                      onChange={(e) => {
                        handleFieldNameChange(e.target.value, field.id)
                      }}
                      label={'Field name'}
                    />

                    <IconButton onClick={() => handleDeleteField(field.id)}>
                      <Close />
                    </IconButton>
                  </Box>
                  {needsOptions && (
                    <Box sx={{ mt: 2, mx: 5 }}>
                      <Accordion
                        elevation={8}
                        expanded={field.optionsOpen}
                        onChange={(_, isExpanded) =>
                          updateField(field.id, { optionsOpen: isExpanded })
                        }
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <b>Options:</b>&nbsp;
                          {field.options.length > 0 &&
                            !field.optionsOpen &&
                            field.options.map((o, i) => (
                              <span key={o.id}>
                                {o.name}
                                {i < field.options.length - 1 && (
                                  <span>,&nbsp;</span>
                                )}
                              </span>
                            ))}
                        </AccordionSummary>
                        <AccordionDetails sx={{ mx: 5 }}>
                          <Box>
                            {field.options.map((o, i) => {
                              return (
                                <Box
                                  key={o.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 'bold',
                                      mr: 2,
                                      width: '20px',
                                    }}
                                  >
                                    {i + 1}
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    value={o.name}
                                    onChange={(e) => {
                                      updateOption(field.id, o.id, {
                                        name: e.target.value,
                                      })
                                    }}
                                    label={'Option name'}
                                  />
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteOption(field.id, o.id)
                                    }
                                  >
                                    <Close />
                                  </IconButton>
                                </Box>
                              )
                            })}
                            <Button
                              disabled={
                                field.options.length >=
                                MAX_ALLOWED_CUSTOM_FIELD_OPTIONS
                              }
                              onClick={() => {
                                updateField(field.id, {
                                  options: [
                                    ...field.options,
                                    {
                                      id: v4(),
                                      name: `Option ${
                                        field.options.length + 1
                                      }`,
                                    },
                                  ],
                                })
                              }}
                              endIcon={<Add />}
                            >
                              Add an option
                            </Button>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Box>
              </DragWrapper>
            )
          })}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            disabled={fieldState.length >= MAX_ALLOWED_CUSTOM_FIELDS}
            onClick={() => handleAddField()}
            endIcon={<Add />}
          >
            Add A Field
          </Button>
        </Box>
      </DndProvider>
    </>
  )
}

const DragWrapper = (props) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.LIST_ITEM,
      canDrag: true,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      item: props.item,
    }),
    [props.item, props.list]
  )

  const dragRef = useRef(null)
  const previewRef = useRef(null)

  drag(dragRef)
  preview(previewRef)

  return (
    <>
      {props.i === 0 && <Drop id={0} onDrop={props.onDrop} />}

      <Box
        ref={previewRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          opacity: isDragging ? 0 : 1,
          height: isDragging ? 0 : null,
        }}
      >
        <Box
          sx={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'move',
            position: 'relative',
            zIndex: 2,
          }}
          ref={dragRef}
        >
          <DragHandle />
        </Box>
        <Box sx={{ flexGrow: 1 }}>{props.children}</Box>
      </Box>

      {!isDragging && <Drop id={props.item.id} onDrop={props.onDrop} />}
    </>
  )
}

const CustomFields = () => {
  const { customFields, setCustomFields, types, saveCustomFields, reset } =
    useCustomFields()

  const { createAlert, createGenericErrorAlert } = useAlert()
  const onSuccess = () =>
    createAlert({
      severity: 'success',
      message: 'Custom Fields saved successfully',
    })

  const onFailure = (e) => {
    createGenericErrorAlert()
  }

  return (
    <>
      <DragAndDropInstructions />
      <CustomFieldsForm
        fieldState={customFields}
        setFieldState={setCustomFields}
        types={types}
      />

      <SettingsFooter
        saveButton={{
          onClick: () => saveCustomFields().then(onSuccess).catch(onFailure),
        }}
        cancelButton={{ label: 'Reset', onClick: () => reset() }}
      />
    </>
  )
}

export default CustomFields
